import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { Browser } from '@capacitor/browser';
import { Stack, ThemeProvider } from '@mui/material';
import dayjs from 'dayjs';

import { useUser } from '@mopla/business-logic';
import { passengerThemeV2 } from '@mopla/mui';
import {
	DTicketPageLayout,
	EMlpIconTypes,
	EMplVariantTypes,
	MplBottomSheet,
	MplIconMessage,
	MplOperatorWordmark,
	MplTicketshopTicketCard,
} from '@mopla/ui';

import { usePassengerAppNavigate } from '../../../hooks';
import { formatMyTicketPrice } from '../ticketshop/formatMyTicketPrice';
import { getMyTicketStatusVariant } from '../ticketshop/getMyTicketStatusVariant';
import { TMyTicketStatus } from '../ticketshop/types';

import {
	MyTicketCancellationErrorModal,
	MyTicketCancellationModal,
	MyTicketCancellationSuccessModal,
	useMyTicketCancellation,
} from './cancellation';
import {
	MyTicketConditions,
	MyTicketDetailSkeleton,
	MyTicketOptions,
} from './components';
import { useMyTicketDetail, useTicketInvoice } from './hooks';
import { isChipcardEligible } from './isChipcardEligible';

/**
 * Statuses that void a ticket: its validity value is struck through and the
 * countdown gives way to this note.
 */
const VOIDED_TEXT_KEY: Partial<Record<TMyTicketStatus, string>> = {
	expired: 'my_ticket_detail.validity_expired',
	cancelled: 'my_ticket_detail.no_valid_ticket',
};

export const MyTicketDetail = () => {
	const { t } = useTranslation('ticketshop');
	const { purchaseId } = useParams<{ purchaseId: string }>();
	const location = useLocation();
	const { navigateBack, navigateToHome, navigateToChipcardRequest } =
		usePassengerAppNavigate();

	// Reached straight from a successful purchase (the purchase screen was
	// replaced in history) — Back must go home, not into the purchase flow.
	const cameFromPurchase = !!(
		location.state as { fromPurchase?: boolean } | null
	)?.fromPurchase;
	const handleBack = useCallback(() => {
		if (cameFromPurchase) {
			navigateToHome();
		} else {
			navigateBack();
		}
	}, [cameFromPurchase, navigateToHome, navigateBack]);

	const { ticket, isLoading } = useMyTicketDetail(purchaseId);
	const { userData } = useUser();

	const {
		sendInvoice,
		handleError: handleInvoiceError,
		errorMessage: invoiceErrorMessage,
		clearError: clearInvoiceError,
	} = useTicketInvoice(purchaseId);

	const {
		step: cancellationStep,
		isPending,
		openConfirmation,
		abortCancellation,
		confirmCancellation,
		closeSuccessModal,
		closeErrorModal,
	} = useMyTicketCancellation(purchaseId);

	// No ticket resolvable after loading (bad id / stale link) → bail to home.
	useEffect(() => {
		if (!isLoading && !ticket) {
			navigateToHome();
		}
	}, [isLoading, ticket, navigateToHome]);

	const display = useMemo(() => {
		if (!ticket) {
			return null;
		}

		const validFrom = ticket.validFrom ? dayjs(ticket.validFrom) : undefined;
		const validUntil = ticket.validUntil ? dayjs(ticket.validUntil) : undefined;

		// An expired validUntil overrides the BE status (AC: "Beendet").
		const isExpiredByDate = !!validUntil && validUntil.isBefore(dayjs());
		const status: TMyTicketStatus =
			isExpiredByDate && ticket.status !== 'cancelled'
				? 'expired'
				: ticket.status ?? 'active';

		const isAbo = ticket.ticketType === 'subscription';
		const isCancellable = status === 'preorder';

		// "Ticket als Chipkarte erhalten" — offered when the BE flags the
		// purchase eligible (see isChipcardEligible).
		const showChipcard = isChipcardEligible(ticket);

		const voidedTextKey = VOIDED_TEXT_KEY[status];
		const voidedText = voidedTextKey ? t(voidedTextKey) : undefined;

		// Kurzstrecke: the validity row shows the entry stop plus the number of
		// covered stops instead of the validFrom-validUntil range, with the
		// validUntil time underneath — dropped once the ticket is voided.
		let stops: string | undefined;
		let validUntilShortTrip: string | undefined;
		if (ticket.ticketType === 'short-trip') {
			if (ticket.entryStopName) {
				stops =
					typeof ticket.stops === 'number'
						? t('my_ticket_detail.stops_value', {
								stop: ticket.entryStopName,
								count: ticket.stops,
						  })
						: ticket.entryStopName;
			}

			if (!voidedText && validUntil) {
				validUntilShortTrip = t('my_ticket_detail.valid_until_short_trip', {
					time: validUntil.format('HH:mm:ss'),
				});
			}
		}

		let remaining: { endDate: dayjs.Dayjs; isPreordered: boolean } | undefined;
		if (status === 'preorder' && validFrom) {
			remaining = { endDate: validFrom, isPreordered: true };
		} else if (status === 'active' && validUntil) {
			remaining = { endDate: validUntil, isPreordered: false };
		}

		// Prefer the purchase-time passenger snapshot from the detail endpoint;
		// fall back to the current user (e.g. while the placeholder paints).
		const fullName = (
			ticket.passengerName?.trim() ||
			`${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`.trim()
		).trim();
		const [firstName, ...restName] = fullName ? fullName.split(' ') : [''];
		const lastName = restName.join(' ');

		return {
			status,
			statusText: t(`my_ticket_detail.status.${status}`),
			statusVariant: getMyTicketStatusVariant(ticket, status),
			validFrom: validFrom ?? dayjs(),
			validUntil: validUntil ?? dayjs(),
			stops,
			validUntilShortTrip,
			voidedText,
			remaining,
			isCancellable,
			showChipcard,
			firstName,
			lastName,
			isAbo,
		};
	}, [ticket, t, userData]);

	const handleRequestHelp = () => {
		if (ticket?.operatorFaqUrl) {
			Browser.open({ url: ticket.operatorFaqUrl });
		}
	};

	const handleRequestChipcard = useCallback(() => {
		if (purchaseId) {
			navigateToChipcardRequest(purchaseId);
		}
	}, [purchaseId, navigateToChipcardRequest]);

	// TODO: Ticketshop – "Ticket entfernen" (remove ticket) for expired/cancelled
	// tickets (see MyTicketOptions). Blocked on a BE remove endpoint
	// (e.g. DELETE /api/v1/me/tickets/{purchaseId}). When available, call it here,
	// then refresh via useRefreshMyTickets() and navigateBack(). Uncomment the
	// button in MyTicketOptions and pass showRemove + onRemove.
	//
	// const showRemove =
	// 	display?.status === 'expired' || display?.status === 'cancelled';
	// const handleRemove = async () => {
	// 	await removeMyTicket(ticket.purchaseId); // BE endpoint (not implemented yet)
	// 	navigateBack();
	// };

	return (
		<ThemeProvider theme={passengerThemeV2}>
			<DTicketPageLayout
				title={ticket?.ticketName ?? ''}
				isTitleLoading={isLoading && !ticket}
				onBack={handleBack}
				scrollable
				stickyHeader
				dataTestId="my-ticket-detail-page"
			>
				{!ticket || !display ? (
					<MyTicketDetailSkeleton />
				) : (
					<Stack gap={5} sx={{ width: '100%', pb: 4 }}>
						<MplOperatorWordmark
							src={ticket.operatorWordmarkUrl}
							dataTestId="my-ticket-detail-banner"
						/>
						<MplTicketshopTicketCard
							firstName={display.firstName}
							lastName={display.lastName}
							priceGroupName={
								ticket.discounted ? ticket.priceGroupName : undefined
							}
							formattedPrice={formatMyTicketPrice(ticket, t)}
							// The VAT rate is optional on the BE model; without it the
							// note would read "Enthält % Umsatzsteuer", so it is dropped.
							vatNote={
								ticket.vatPercent != null
									? t('my_ticket_detail.vat_note', {
											percent: ticket.vatPercent,
									  })
									: undefined
							}
							statusText={display.statusText}
							statusVariant={display.statusVariant}
							validityLabel={t('my_ticket_detail.validity_label')}
							validFrom={display.validFrom}
							validUntil={display.validUntil}
							stops={display.stops}
							validUntilShortTrip={display.validUntilShortTrip}
							voidedText={display.voidedText}
							isCancelled={display.status === 'cancelled'}
							isDiscounted={ticket.discounted}
							remaining={display.remaining}
							zonesLabel={t('my_ticket_detail.zones_label')}
							zoneCodes={ticket.zoneCodes}
							isWholeNetwork={ticket.wholeNetwork}
							wholeNetworkLabel={t('ticket_detail.zones_whole_network_label')}
							ticketIdLabel={t('my_ticket_detail.ticket_id_label')}
							ticketCode={ticket.ticketCode ?? ''}
							secretWord={
								// Inspection word only shows on active tickets; the BE
								// omits it otherwise, so no badge (and no fallback).
								display.status === 'active' && ticket.secretWord
									? ticket.secretWord.toUpperCase()
									: undefined
							}
							operatorIconUrl={ticket.operatorIconUrl}
							dataTestId="my-ticket-detail-card"
						/>

						{display.isAbo && (
							<MplIconMessage
								type={EMlpIconTypes.Information}
								variant={EMplVariantTypes.Info}
								message={t('my_ticket_detail.name_change_info')}
							/>
						)}

						<MyTicketConditions
							purchasedFrom={ticket.operatorName}
							showControlMedium={ticket.ticketType === 'subscription'}
							isDiscounted={ticket.discounted}
						/>

						<MyTicketOptions
							showCancellation={display.isCancellable}
							showChipcard={display.showChipcard}
							showHelp={!!ticket.operatorFaqUrl}
							onSendInvoice={sendInvoice}
							onInvoiceError={handleInvoiceError}
							onRequestHelp={handleRequestHelp}
							onCancel={openConfirmation}
							onRequestChipcard={handleRequestChipcard}
						/>
					</Stack>
				)}
			</DTicketPageLayout>

			<MyTicketCancellationModal
				open={cancellationStep === 'confirm'}
				isLoading={isPending}
				onConfirm={confirmCancellation}
				onAbort={abortCancellation}
			/>
			<MyTicketCancellationSuccessModal
				open={cancellationStep === 'success'}
				onClose={closeSuccessModal}
			/>
			<MyTicketCancellationErrorModal
				open={cancellationStep === 'error'}
				onClose={closeErrorModal}
			/>

			<MplBottomSheet
				open={!!invoiceErrorMessage}
				onClose={clearInvoiceError}
				showPuller
				testId="my-ticket-detail-invoice-error"
			>
				<Stack sx={{ px: 2, pt: 2, pb: 3 }}>
					<MplIconMessage
						type={EMlpIconTypes.Warning}
						variant={EMplVariantTypes.Error}
						message={invoiceErrorMessage ?? ''}
					/>
				</Stack>
			</MplBottomSheet>
		</ThemeProvider>
	);
};
